import { useState } from 'react';
import {
  MIDDLEWARE_CATALOGUE,
  type SchoolMiddlewaresStatus,
  type TenantMiddlewaresView,
} from '@teachpartner/contracts';
import { describeError } from '../../api/client';
import { useServices } from '../../app-context';
import { ErrorBanner } from '../../components/badges';
import { useAsync } from '../../hooks/use-async';

type Push = NonNullable<TenantMiddlewaresView['push']>;

function SyncBadge({ view }: { view: TenantMiddlewaresView | null }) {
  if (!view?.reported) return <span className="badge badge-muted">not reported</span>;
  return view.inSync ? (
    <span className="badge badge-ok">in sync</span>
  ) : (
    <span className="badge badge-warn">waiting for the school</span>
  );
}

function reportedState(reported: SchoolMiddlewaresStatus | null, id: string): string {
  if (!reported) return '—';
  return reported.allowed.includes(id) ? 'allowed' : 'blocked';
}

function configuredState(reported: SchoolMiddlewaresStatus | null, id: string): string {
  const configured = reported?.configured[id];
  if (configured === undefined) return '—';
  return configured ? '✅ configured' : '❌ not configured';
}

function pushMessage(push: Push): string {
  return push.ok
    ? 'Saved and sent to the school.'
    : `Saved. Not delivered yet (${push.error ?? 'unknown error'}); central keeps re-sending until the school reports it.`;
}

/**
 * Host Admin allow-list of plug-in middlewares (plan §5). The school decides which modules to
 * switch on; it reports only "configured yes/no" per middleware, never its settings.
 */
export function MiddlewaresSection({ code }: { code: string }) {
  const { api } = useServices();
  const loaded = useAsync(() => api.middlewares(code), [api, code]);
  const [saved, setSaved] = useState<TenantMiddlewaresView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const view = saved ?? loaded.data;

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string, allow: boolean) =>
    run(async () => {
      const current = view?.allowed ?? [];
      const next = allow ? [...current, id] : current.filter((x) => x !== id);
      setSaved(await api.setMiddlewares(code, next));
    });

  const checkNow = () =>
    run(async () => {
      await api.health(code, true);
      setSaved(null);
      loaded.reload();
    });

  return (
    <section aria-label="Middlewares">
      <h2>
        Middlewares <SyncBadge view={view} />
      </h2>
      <p>Which plug-ins this school may use. The school&apos;s Tenant Admin switches modules on.</p>
      <ErrorBanner message={loaded.error ?? error} />
      {saved?.push && (
        <div className={`banner ${saved.push.ok ? 'banner-info' : 'banner-warn'}`}>
          {pushMessage(saved.push)}
        </div>
      )}
      <table>
        <thead>
          <tr>
            <th>Middleware</th>
            <th>Allowed</th>
            <th>School reports</th>
            <th>Settings</th>
          </tr>
        </thead>
        <tbody>
          {MIDDLEWARE_CATALOGUE.map((m) => {
            const allowed = view?.allowed.includes(m.id) ?? false;
            return (
              <tr key={m.id}>
                <td>{m.title}</td>
                <td>
                  <label className="check">
                    <input
                      type="checkbox"
                      role="switch"
                      aria-label={`Allow ${m.title}`}
                      checked={allowed}
                      disabled={!view || busy}
                      onChange={(e) => void toggle(m.id, e.target.checked)}
                    />
                    {allowed ? 'allowed' : 'blocked'}
                  </label>
                </td>
                <td>{reportedState(view?.reported ?? null, m.id)}</td>
                <td>{configuredState(view?.reported ?? null, m.id)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button type="button" onClick={() => void checkNow()} disabled={busy || loaded.loading}>
        Check school now
      </button>
    </section>
  );
}
